import type {
  ProblemStatementRating,
  ProblemStatementRollup,
  ProblemStatementRuleSnapshot,
  PsRuleHumanReview,
  PsRuleHumanReviewRun,
} from "@/services/api/case-checking";
import {
  agentStatusToHumanCall,
  normalizeRuleStatus,
  type RuleHumanCall,
  type RuleIssueStatus,
} from "@/components/case-checking/problemStatementRuleIssues";

export const RULE_CALL_LABEL: Record<RuleHumanCall, string> = {
  pass: "Pass",
  fail: "Fail",
  unclear: "Unclear",
  na: "N/A",
  excluded: "Excluded",
};

export function ruleReviewKey(problemStatementId: string, ruleId: string): string {
  return `${problemStatementId}::${ruleId}`;
}

type PsRuleHumanReviewWire = PsRuleHumanReview & { reviewedAt?: string | null };

/** API + session merges should use snake_case; accept camelCase defensively. */
export function humanRuleReviewSavedAt(review: PsRuleHumanReview | null | undefined): string | null {
  if (!review) return null;
  const wire = review as PsRuleHumanReviewWire;
  const at = wire.reviewed_at ?? wire.reviewedAt ?? null;
  if (at == null) return null;
  if (typeof at === "string" && at.trim() === "") return null;
  return at;
}

export function isSavedHumanRuleReview(review: PsRuleHumanReview | null | undefined): boolean {
  return humanRuleReviewSavedAt(review) != null;
}

export function normalizePsRuleHumanReview(review: PsRuleHumanReviewWire): PsRuleHumanReview {
  const reviewed_at = humanRuleReviewSavedAt(review);
  if (reviewed_at === review.reviewed_at) return review;
  return { ...review, reviewed_at };
}

/** Keep saved timestamps when a PATCH response omits or clears reviewed_at on known keys. */
export function mergeHumanRuleReviews(
  existing: PsRuleHumanReview[],
  incoming: PsRuleHumanReview[],
): PsRuleHumanReview[] {
  if (incoming.length === 0 && existing.length > 0) {
    return existing.map((r) => normalizePsRuleHumanReview(r as PsRuleHumanReviewWire));
  }
  const byKey = new Map<string, PsRuleHumanReview>();
  for (const raw of existing) {
    const review = normalizePsRuleHumanReview(raw as PsRuleHumanReviewWire);
    byKey.set(ruleReviewKey(review.problem_statement_id, review.rule_id), review);
  }
  for (const raw of incoming) {
    const next = normalizePsRuleHumanReview(raw as PsRuleHumanReviewWire);
    const key = ruleReviewKey(next.problem_statement_id, next.rule_id);
    const prev = byKey.get(key);
    const reviewed_at = humanRuleReviewSavedAt(next) ?? humanRuleReviewSavedAt(prev) ?? null;
    byKey.set(key, normalizePsRuleHumanReview({ ...prev, ...next, reviewed_at }));
  }
  return [...byKey.values()];
}

export function normalizePsRuleHumanReviewRun(run: PsRuleHumanReviewRun): PsRuleHumanReviewRun {
  return {
    ...run,
    reviews: (run.reviews ?? []).map((r) => normalizePsRuleHumanReview(r as PsRuleHumanReviewWire)),
  };
}

export function humanCallToRuleStatus(call: RuleHumanCall): RuleIssueStatus {
  if (call === "pass") return "passed";
  if (call === "fail") return "failed";
  if (call === "na" || call === "excluded") return "na";
  return "unclear";
}

export function ruleStatusToApiString(status: RuleIssueStatus): string {
  if (status === "passed") return "passed";
  if (status === "failed") return "failed";
  if (status === "na") return "na";
  return "unclear";
}

export function effectiveRuleStatus(
  agentStatus: string,
  override?: PsRuleHumanReview | null,
): RuleIssueStatus {
  if (isSavedHumanRuleReview(override)) return humanCallToRuleStatus(override!.call);
  return normalizeRuleStatus(agentStatus);
}

export type RuleOverrideMap = Map<string, PsRuleHumanReview>;

export function buildRuleOverrideMap(reviews: PsRuleHumanReview[]): RuleOverrideMap {
  const map: RuleOverrideMap = new Map();
  for (const raw of reviews) {
    const review = normalizePsRuleHumanReview(raw as PsRuleHumanReviewWire);
    map.set(ruleReviewKey(review.problem_statement_id, review.rule_id), review);
  }
  return map;
}

export interface EffectiveRuleView extends ProblemStatementRuleSnapshot {
  agentStatus: RuleIssueStatus;
  effectiveStatus: RuleIssueStatus;
  humanChanged: boolean;
  override?: PsRuleHumanReview;
}

export interface PsRuleChangeQuote {
  ruleId: string;
  label: string;
  agentCall: RuleHumanCall;
  humanCall: RuleHumanCall;
  note: string;
  verdictChanged: boolean;
}

function countEffectiveStatuses(rules: EffectiveRuleView[]): Record<string, number> {
  const counts = { passed: 0, failed: 0, unclear: 0, na: 0, pending: 0 };
  for (const rule of rules) {
    const key = rule.effectiveStatus in counts ? rule.effectiveStatus : "unclear";
    counts[key] += 1;
  }
  return counts;
}

function ratingFromEffectiveRules(
  rules: EffectiveRuleView[] | { effectiveStatus: RuleIssueStatus }[],
  rollupKind: ProblemStatementRollup["rollup_kind"],
  fallback: ProblemStatementRating,
): ProblemStatementRating {
  if (rollupKind === "display_only" || rollupKind === "final") return fallback;
  const nonNa = rules.filter((r) => r.effectiveStatus !== "na");
  if (nonNa.length === 0) return "na";
  if (nonNa.some((r) => r.effectiveStatus === "failed")) return "fail";
  if (nonNa.some((r) => r.effectiveStatus === "unclear")) return "unclear";
  return "pass";
}

/** PS rating from effective rule statuses (always recomputed — not gated on human edits in section). */
export function rollupRatingFromEffectiveRules(
  rollup: ProblemStatementRollup,
  overrides: RuleOverrideMap,
): ProblemStatementRating {
  const effectiveRules = rollup.rules.map((rule) => ({
    effectiveStatus: effectiveRuleStatus(
      rule.status,
      overrides.get(ruleReviewKey(rollup.id, rule.rule_id)),
    ),
  }));
  return ratingFromEffectiveRules(effectiveRules, rollup.rollup_kind, rollup.rating);
}

function ratingFromEffectiveRuleViews(
  rules: EffectiveRuleView[],
  rollupKind: ProblemStatementRollup["rollup_kind"],
  fallback: ProblemStatementRating,
): ProblemStatementRating {
  return ratingFromEffectiveRules(rules, rollupKind, fallback);
}

export function applyRuleOverridesToRollup(
  rollup: ProblemStatementRollup,
  overrides: RuleOverrideMap,
): { rollup: ProblemStatementRollup; effectiveRules: EffectiveRuleView[] } {
  const effectiveRules: EffectiveRuleView[] = rollup.rules.map((rule) => {
    const override = overrides.get(ruleReviewKey(rollup.id, rule.rule_id));
    const agentStatus = normalizeRuleStatus(rule.status);
    const effectiveStatus = effectiveRuleStatus(rule.status, override);
    const humanChanged = isSavedHumanRuleReview(override);
    return {
      ...rule,
      agentStatus,
      effectiveStatus,
      humanChanged,
      override,
      status: ruleStatusToApiString(effectiveStatus),
    };
  });

  const hasHumanChanges = effectiveRules.some((r) => r.humanChanged);
  if (!hasHumanChanges) {
    return { rollup, effectiveRules };
  }

  const counts = countEffectiveStatuses(effectiveRules);
  const rating = ratingFromEffectiveRuleViews(effectiveRules, rollup.rollup_kind, rollup.rating);
  const rules: ProblemStatementRuleSnapshot[] = effectiveRules.map(
    ({ agentStatus: _a, effectiveStatus: _e, humanChanged: _h, override: _o, ...snap }) => snap,
  );

  return {
    rollup: { ...rollup, rules, counts, rating },
    effectiveRules,
  };
}

export function applyRuleOverridesToRollups(
  rollups: ProblemStatementRollup[],
  overrides: RuleOverrideMap,
): ProblemStatementRollup[] {
  return rollups.map((rollup) => applyRuleOverridesToRollup(rollup, overrides).rollup);
}

export function collectPsRuleChangeQuotes(
  rollup: ProblemStatementRollup,
  overrides: RuleOverrideMap,
): PsRuleChangeQuote[] {
  const quotes: PsRuleChangeQuote[] = [];
  for (const rule of rollup.rules) {
    const override = overrides.get(ruleReviewKey(rollup.id, rule.rule_id));
    if (!isSavedHumanRuleReview(override)) continue;
    const agentCall = agentStatusToHumanCall(rule.status);
    const humanCall = override.call;
    quotes.push({
      ruleId: rule.rule_id,
      label: rule.label || rule.rule_id,
      agentCall,
      humanCall,
      note: override.notes?.trim() ?? "",
      verdictChanged: humanCall !== agentCall,
    });
  }
  return quotes;
}

export function effectivePsRating(
  rollup: ProblemStatementRollup,
  overrides: RuleOverrideMap,
): ProblemStatementRating {
  return applyRuleOverridesToRollup(rollup, overrides).rollup.rating;
}
